import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ACCESS_REQUEST_STATUS_LABEL,
  type AccessRequestAdminRow,
  type AccessRequestStatus,
} from '@igf-minar/shared'
import { api } from '../lib/api'
import { useCurrentUser, isAdminRole } from '../lib/auth'
import { formatDateTimeShort } from '../lib/dates'
import { useFeatureFlags } from '../lib/features'
import { NotFound } from '../components/NotFound'

export const Route = createFileRoute('/admin/umsoknir')({
  component: AdminAccessRequestsPage,
})

const FILTERS: { value: string; label: string }[] = [
  { value: 'approved', label: 'Til afgreiðslu' },
  { value: 'pending', label: 'Bíða prókúruhafa' },
  { value: 'completed', label: 'Afgreiddar' },
  { value: 'rejected', label: 'Hafnað' },
  { value: 'all', label: 'Allar' },
]

const STATUS_STYLE: Record<string, string> = {
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  completed: 'bg-slate-100 text-slate-600 border-slate-200',
  rejected: 'bg-red-50 text-red-600 border-red-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
  expired: 'bg-slate-100 text-slate-500 border-slate-200',
}

function AdminAccessRequestsPage() {
  const { data: me, isLoading: meLoading } = useCurrentUser()
  const flags = useFeatureFlags()
  const qc = useQueryClient()
  const isAdmin = isAdminRole(me)
  const [filter, setFilter] = useState('approved')
  const [result, setResult] = useState<{ email: string; tempPassword?: string; existingUser?: boolean; emailSent: boolean } | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'access-requests', filter],
    queryFn: () => api.admin.listAccessRequests(filter),
    enabled: isAdmin && flags.accessRequests,
  })

  const completeMut = useMutation({
    mutationFn: (id: string) => api.admin.completeAccessRequest(id),
    onSuccess: (res) => {
      setActionError(null)
      setResult(res)
      qc.invalidateQueries({ queryKey: ['admin', 'access-requests'] })
      qc.invalidateQueries({ queryKey: ['companies'] })
    },
    onError: (err) => setActionError((err as Error).message),
  })

  const cancelMut = useMutation({
    mutationFn: (id: string) => api.admin.cancelAccessRequest(id),
    onSuccess: () => {
      setActionError(null)
      qc.invalidateQueries({ queryKey: ['admin', 'access-requests'] })
    },
    onError: (err) => setActionError((err as Error).message),
  })

  if (meLoading || flags.isLoading) return <PageShell><p className="text-[var(--muted)]">Hleð…</p></PageShell>
  if (!flags.accessRequests) return <NotFound />

  if (!isAdmin) {
    return (
      <PageShell>
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-6 text-amber-900">
          <h2 className="font-semibold">Enginn aðgangur</h2>
          <p className="mt-1 text-sm">Aðeins admin má afgreiða aðgangsumsóknir.</p>
        </div>
      </PageShell>
    )
  }

  const requests = data?.requests ?? []

  return (
    <PageShell>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--fg)]">Aðgangsumsóknir</h1>
        <p className="text-sm text-[var(--muted)] mt-1">
          Umsóknir af innskráningarsíðunni. Aðgangur verður fyrst til þegar prókúruhafi hefur
          samþykkt og þjónustufulltrúi ýtir á „Stofna aðgang“.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
              filter === f.value
                ? 'border-[var(--brand)] bg-[var(--brand)] text-[var(--on-brand)]'
                : 'border-[var(--border)] bg-[var(--surface)] text-[var(--muted)] hover:border-[var(--brand)]'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {result && (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <p className="font-medium">
            {result.existingUser
              ? `Aðgangur var þegar til fyrir ${result.email} — fyrirtækinu bætt við hann.`
              : `Aðgangur stofnaður fyrir ${result.email}.`}
          </p>
          {result.tempPassword && (
            <p className="mt-1">
              Bráðabirgðalykilorð: <code className="font-mono">{result.tempPassword}</code>
              {result.emailSent
                ? ' — sent í tölvupósti.'
                : ' — tölvupóstur fór ekki, sendu það handvirkt.'}
            </p>
          )}
          <button
            type="button"
            onClick={() => setResult(null)}
            className="mt-2 text-xs underline"
          >
            Loka
          </button>
        </div>
      )}

      {actionError && <p className="mb-4 text-red-500">{actionError}</p>}
      {isLoading && <p className="text-[var(--muted)]">Sæki umsóknir…</p>}
      {error && <p className="text-red-500">{(error as Error).message}</p>}

      {data && requests.length === 0 && (
        <p className="text-[var(--muted)]">Engar umsóknir í þessum flokki.</p>
      )}

      <div className="space-y-3">
        {requests.map((r) => (
          <RequestCard
            key={r.id}
            request={r}
            busy={completeMut.isPending || cancelMut.isPending}
            onComplete={() => completeMut.mutate(r.id)}
            onCancel={() => cancelMut.mutate(r.id)}
          />
        ))}
      </div>
    </PageShell>
  )
}

function RequestCard({ request: r, busy, onComplete, onCancel }: {
  request: AccessRequestAdminRow
  busy: boolean
  onComplete: () => void
  onCancel: () => void
}) {
  const company = r.companyName || `kt. ${r.companyKennitala}`
  const status = r.status as AccessRequestStatus

  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-5 shadow-[var(--elev-sm)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-semibold text-[var(--fg)]">{company}</h2>
          <p className="text-xs text-[var(--muted)] mt-0.5">
            kt. {r.companyKennitala} · sótt um {formatDateTimeShort(r.createdAt)}
          </p>
        </div>
        <span
          className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${
            STATUS_STYLE[status] ?? 'bg-slate-100 text-slate-600 border-slate-200'
          }`}
        >
          {ACCESS_REQUEST_STATUS_LABEL[status] ?? status}
        </span>
      </div>

      {r.selfApproved && (
        <p className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Umsækjandi skráði sjálfan sig sem prókúruhafa — staðfestu prókúru í fyrirtækjaskrá áður en
          aðgangur er stofnaður.
        </p>
      )}

      <div className="mt-4 grid gap-4 text-sm sm:grid-cols-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--faint)]">Umsækjandi</p>
          <p className="mt-1 text-[var(--fg)]">{r.applicantName}</p>
          <p className="text-[var(--muted)]">kt. {r.applicantKennitala}</p>
          <p className="text-[var(--muted)]">{r.applicantEmail}</p>
          <p className="text-[var(--muted)]">{r.applicantPhone}</p>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--faint)]">Prókúruhafi</p>
          <p className="mt-1 text-[var(--fg)]">{r.approverName}</p>
          <p className="text-[var(--muted)]">{r.approverEmail}</p>
          {r.decidedAt && (
            <p className="text-[var(--muted)]">Svaraði {formatDateTimeShort(r.decidedAt)}</p>
          )}
        </div>
      </div>

      {r.decisionNote && (
        <p className="mt-3 rounded border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-sm text-[var(--fg-soft)]">
          Athugasemd prókúruhafa: {r.decisionNote}
        </p>
      )}

      {(status === 'approved' || status === 'pending') && (
        <div className="mt-4 flex flex-wrap gap-2">
          {status === 'approved' && (
            <button
              type="button"
              disabled={busy}
              onClick={onComplete}
              className="rounded-lg bg-[var(--brand)] px-3.5 py-2 text-sm font-semibold text-[var(--on-brand)] hover:bg-[var(--brand-strong)] disabled:opacity-50"
            >
              Stofna aðgang
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="rounded-lg border border-[var(--border)] px-3.5 py-2 text-sm font-medium text-[var(--muted)] hover:border-red-300 hover:text-red-600 disabled:opacity-50"
          >
            Fella niður
          </button>
        </div>
      )}
    </div>
  )
}

function PageShell({ children }: { children: React.ReactNode }) {
  return <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</div>
}
